import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { MusicIcon, AudioPlayer, ReleaseProgressBar } from './index';
import { useTranslation } from 'react-i18next';
import { Release } from '../../types';
import { downloadFile } from '../../utils/downloadFile';

// Kopyalama butonu bileşeni
const CopyButton: React.FC<{ text: string; className?: string }> = ({ text, className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Kopyalama başarısız:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`ml-2 p-1 rounded hover:bg-white/20 transition-colors ${className}`}
      title="Kopyala"
    >
      {copied ? (
        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-white/60 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
};

// Lyrics'teki [] ifadelerini temizleyen fonksiyon
const cleanLyrics = (lyrics: string): string => {
  return lyrics.replace(/\[.*?\]\n?/g, '').trim();
};

interface TrackDetailViewProps {
  release: Release;
  isAdmin?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canSubmitForApproval?: boolean;
  onDelete?: () => void;
  onSubmitForApproval?: () => void;
  onStatusChange?: (newStatus: Release['status']) => void;
  onClose?: () => void;
  backToPath?: string;
  backToText?: string;
}

const TrackDetailView: React.FC<TrackDetailViewProps> = ({
  release,
  isAdmin = false,
  canEdit = false,
  canDelete = false,
  canSubmitForApproval = false,
  onDelete,
  onSubmitForApproval,
  onStatusChange,
  onClose,
  backToPath = '/tracks',
  backToText = 'tracks.backToTracks'
}) => {
  const { getAudioFileUrl, getHybridAudioFileUrl, getHybridCoverArtUrl } = useData();
  const { t } = useTranslation();
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  useEffect(() => {
    if (release && release.tracks && release.tracks.length > 0) {
      const track = release.tracks[0];
      const trackId = track.id || `${release.id}_track_1`;
      
      // Handle cover art - check for S3 storage first
      if (release.cover_storage_type === 's3' && release.s3_cover_key) {
        getHybridCoverArtUrl(release.id)
          .then(hybridCoverUrl => {
            if (hybridCoverUrl) {
              setCoverUrl(hybridCoverUrl);
            }
          })
          .catch(error => {
            console.error('Error loading S3 cover art:', error);
            // Fallback to legacy cover_art field
            setCoverUrl(release.cover_art || null);
          });
      } else if (release.cover_art) {
        setCoverUrl(release.cover_art);
      }
      
      // Use hybrid URL function for all audio files (supports both S3 and Supabase)
      getHybridAudioFileUrl(trackId)
        .then(hybridUrl => {
          if (hybridUrl) {
            setAudioUrl(hybridUrl);
            console.log('🎵 Audio URL retrieved:', hybridUrl);

            // Admin için AudioPlayer component'i kullanabilmek adına File objesi oluştur
            if (isAdmin && track.audio_storage_type === 'supabase') {
              fetch(hybridUrl)
                .then(response => response.blob())
                .then(blob => {
                  const file = new File([blob], `${track.title}.wav`, { type: 'audio/wav' });
                  setAudioFile(file);
                })
                .catch(error => {
                  console.error('Audio file yükleme hatası:', error);
                });
            }
          } else {
            console.warn('⚠️ Audio URL alınamadı');
          }
        })
        .catch(error => {
          console.error('Hybrid audio URL getirme hatası:', error);
        });
    }
  }, [release, getAudioFileUrl, getHybridAudioFileUrl, getHybridCoverArtUrl, isAdmin]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'submitted':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'draft':
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published':
        return t('status.published');
      case 'approved':
        return t('status.approved');
      case 'pending':
        return t('status.pending');
      case 'submitted':
        return t('status.submitted');
      case 'draft':
        return t('status.draft');
      case 'rejected':
        return t('status.rejected');
      default:
        return status;
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadCover = () => {
    if (!coverUrl) return;
    downloadFile(coverUrl);
  };

  const handleDownloadAudio = () => {
    if (!audioUrl) return;
    downloadFile(audioUrl);
  };

  const track = release.tracks[0]; // İlk parçayı alıyoruz

  // If onClose prop is provided, render as modal
  if (onClose) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/20 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-xl font-bold text-white">{release.title}</h2>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-6">
            {/* Release Info */}
            <div className="glass-card">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="mb-4 lg:mb-0">
                  <h3 className="text-lg font-bold text-white">{release.title}</h3>
                  <p className="mt-1 text-sm text-white/70">
                    {release.artist_display_name} • {release.type} • {new Date(release.release_date).toLocaleDateString('tr-TR')}
                  </p>
                </div>
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(release.status)}`}>
                  {getStatusText(release.status)}
                </span>
              </div>

              {/* Platform Hesapları (Modal için) */}
              {release.artists?.is_artist_exist_on_platforms && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <span className="text-sm text-white/60 mb-3 block">Platform Hesapları:</span>
                  <div className="flex flex-wrap gap-2">
                    {release.artists.spotify_artist_profile_url && (
                      <a
                        href={release.artists.spotify_artist_profile_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 text-green-300 border border-green-600/30 rounded-lg transition-colors duration-200 text-sm"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                        </svg>
                        Spotify
                      </a>
                    )}
                    {release.artists.apple_music_artist_profile_url && (
                      <a
                        href={release.artists.apple_music_artist_profile_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 bg-pink-600/20 hover:bg-pink-600/30 text-pink-300 border border-pink-600/30 rounded-lg transition-colors duration-200 text-sm"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.132c-.317-1.314-1.062-2.31-2.17-3.025a5.334 5.334 0 00-2.186-.681 39.246 39.246 0 00-2.956-.1h-7.64c-.92 0-1.841.024-2.759.1a5.4 5.4 0 00-2.186.681c-1.108.715-1.853 1.711-2.17 3.025a9.123 9.123 0 00-.24 2.132c-.024.776-.035 1.552-.035 2.328v7.096c0 .776.011 1.552.035 2.328.024.776.086 1.454.24 2.132.317 1.314 1.062 2.31 2.17 3.025a5.334 5.334 0 002.186.681c.92.076 1.839.1 2.759.1h7.64c.92 0 1.841-.024 2.759-.1a5.334 5.334 0 002.186-.681c1.108-.715 1.853-1.711 2.17-3.025.154-.678.216-1.356.24-2.132.024-.776.035-1.552.035-2.328V8.452c0-.776-.011-1.552-.035-2.328zm-4.165 6.155v.186a4.337 4.337 0 01-.748 2.549 3.511 3.511 0 01-2.757 1.48c-.895 0-1.648-.396-2.181-1.162-.591-.852-.895-1.915-.895-3.18V5.986h1.972v6.197c0 .936.155 1.647.437 2.139.296.484.724.739 1.307.739.486 0 .916-.196 1.241-.599.3-.36.451-.791.451-1.316v-7.16h1.972v4.943l.201-.014z"/>
                        </svg>
                        Apple Music
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Track Info */}
            {track && (
              <div className="glass-card">
                <h4 className="text-lg font-semibold text-white mb-4">Şarkı Detayları</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-white/60">Şarkı Adı:</span>
                    <p className="text-white">{track.title}</p>
                  </div>
                  <div>
                    <span className="text-sm text-white/60">Süre:</span>
                    <p className="text-white">{track.duration}</p>
                  </div>
                  <div>
                    <span className="text-sm text-white/60">Tür:</span>
                    <p className="text-white">{release.genre}</p>
                  </div>
                  <div>
                    <span className="text-sm text-white/60">Dil:</span>
                    <p className="text-white">{track.language || release.language}</p>
                  </div>
                </div>
                
                {/* Audio Player */}
                {audioUrl && (
                  <div className="mt-4">
                    <AudioPlayer audioUrl={audioUrl} />
                  </div>
                )}
              </div>
            )}

            {/* Rights Info */}
            {release.rights && (
              <div className="glass-card">
                <h4 className="text-lg font-semibold text-white mb-4">Yayın Hakları</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {release.rights.isrc && (
                    <div>
                      <span className="text-sm text-white/60">ISRC:</span>
                      <p className="text-white font-mono flex items-center">
                        {release.rights.isrc}
                        <CopyButton text={release.rights.isrc} />
                      </p>
                    </div>
                  )}
                  {release.rights.upc && (
                    <div>
                      <span className="text-sm text-white/60">UPC:</span>
                      <p className="text-white font-mono flex items-center">
                        {release.rights.upc}
                        <CopyButton text={release.rights.upc} />
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Regular full-page view
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="glass-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-white">{release.title}</h1>
            <p className="mt-1 text-sm text-white/70">
              {isAdmin && release.artists?.display_name && `${release.artists.display_name} • `}
              {release.type} • {new Date(release.release_date).toLocaleDateString('tr-TR')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(release.status)}`}>
                {getStatusText(release.status)}
              </span>
              {isAdmin && onStatusChange && (
                <button
                  onClick={() => setShowStatusModal(true)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm font-medium text-white transition-all duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Status Değiştir
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              {canEdit && (
                <Link to={`${isAdmin ? '/admin' : ''}/tracks/edit/${release.id}`} className="btn-primary">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('common.edit')}
                </Link>
              )}
              {canSubmitForApproval && onSubmitForApproval && (
                <button
                  onClick={onSubmitForApproval}
                  className="bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  {t('common.submitForApproval')}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="btn-danger"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  {isDeleting ? t('common.deleting') : t('common.delete')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="glass-card">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
          <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('progress.trackProgress')}
        </h3>
        <ReleaseProgressBar 
          status={release.status} 
          rejectReason={release.reject_reason}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cover Art */}
        <div className="lg:col-span-1">
          <div className="glass-card">
            <div className="aspect-square relative group">
              {coverUrl ? (
                <>
                  <img
                    src={coverUrl}
                    alt={release.title}
                    className="w-full h-full object-cover rounded-xl border border-white/20"
                  />
                  {isAdmin && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl flex items-center justify-center">
                      <button
                        onClick={handleDownloadCover}
                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg text-sm font-medium text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 transform hover:scale-105 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        {t('common.download')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center rounded-xl border border-white/20">
                  <MusicIcon size="xl" />
                </div>
              )}
            </div>
            {isAdmin && coverUrl && (
              <div className="mt-4 text-center">
                <button
                  onClick={handleDownloadCover}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg text-sm font-medium text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 transform hover:scale-105"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  {t('admin.downloadCover')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="glass-card">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('tracks.basicInfo')}
            </h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {isAdmin && (
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <dt className="text-sm font-medium text-white/70 mb-2">{t('admin.artist')}</dt>
                  <dd className="text-lg font-semibold text-white flex items-center">
                    {release.artists?.display_name || 'Bilinmeyen Sanatçı'}
                    {release.artists?.display_name && <CopyButton text={release.artists.display_name} />}
                  </dd>
                </div>
              )}
              {isAdmin && (release.artists?.first_name || release.artists?.last_name) && (
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <dt className="text-sm font-medium text-white/70 mb-2">Ad/Soyad</dt>
                  <dd className="text-lg font-semibold text-white flex items-center">
                    {[release.artists?.first_name?.trim(), release.artists?.last_name?.trim()].filter(Boolean).join(' ')}
                    <CopyButton text={[release.artists?.first_name?.trim(), release.artists?.last_name?.trim()].filter(Boolean).join(' ')} />
                  </dd>
                </div>
              )}
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.releaseType')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {release.type}
                  <CopyButton text={release.type} />
                </dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.mainGenre')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {release.genre}
                  <CopyButton text={release.genre} />
                </dd>
              </div>
              {release.subgenre && (
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.subGenre')}</dt>
                  <dd className="text-lg font-semibold text-white flex items-center">
                    {release.subgenre}
                    <CopyButton text={release.subgenre} />
                  </dd>
                </div>
              )}
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.language')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {release.language}
                  <CopyButton text={release.language} />
                </dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.releaseDate')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {new Date(release.release_date).toLocaleDateString('tr-TR')}
                  <CopyButton text={new Date(release.release_date).toLocaleDateString('tr-TR')} />
                </dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.createdDate')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {new Date(release.created_at).toLocaleDateString('tr-TR')}
                  <CopyButton text={new Date(release.created_at).toLocaleDateString('tr-TR')} />
                </dd>
              </div>
              {isAdmin && (
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.status')}</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${getStatusColor(release.status)}`}>
                      {getStatusText(release.status)}
                    </span>
                  </dd>
                </div>
              )}
              {isAdmin && release.app_info && (
                <div className="bg-blue-500/10 p-4 rounded-xl border border-blue-500/30">
                  <dt className="text-sm font-medium text-blue-300 mb-2">{t('admin.sourceApp')}</dt>
                  <dd className="text-lg font-semibold text-blue-200">{release.app_info}</dd>
                </div>
              )}
            </dl>
            {release.description && (
              <div className="mt-6 bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.description')}</dt>
                <dd className="text-white leading-relaxed">{release.description}</dd>
              </div>
            )}
          </div>

          {/* Artist & User Information - Admin Only */}
          {isAdmin && release.artists && (
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Sanatçı ve Kullanıcı Bilgileri
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Artist Information */}
                <div className="col-span-2">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                    </svg>
                    Sanatçı Bilgileri
                  </h4>
                </div>

                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <dt className="text-sm font-medium text-white/70 mb-2">Sanatçı ID</dt>
                  <dd className="text-sm font-mono text-white break-all flex items-center">
                    {release.artist_id || 'N/A'}
                    {release.artist_id && <CopyButton text={release.artist_id} />}
                  </dd>
                </div>

                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <dt className="text-sm font-medium text-white/70 mb-2">Görünür İsim</dt>
                  <dd className="text-lg font-semibold text-white flex items-center">
                    {release.artists.display_name || 'N/A'}
                    {release.artists.display_name && <CopyButton text={release.artists.display_name} />}
                  </dd>
                </div>

                {(release.artists.first_name || release.artists.last_name) && (
                  <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                    <dt className="text-sm font-medium text-white/70 mb-2">Ad/Soyad</dt>
                    <dd className="text-lg font-semibold text-white flex items-center">
                      {[release.artists.first_name?.trim(), release.artists.last_name?.trim()].filter(Boolean).join(' ')}
                      <CopyButton text={[release.artists.first_name?.trim(), release.artists.last_name?.trim()].filter(Boolean).join(' ')} />
                    </dd>
                  </div>
                )}

                {release.artists.email && (
                  <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                    <dt className="text-sm font-medium text-white/70 mb-2">Email</dt>
                    <dd className="text-white font-mono text-sm break-all flex items-center">
                      {release.artists.email}
                      <CopyButton text={release.artists.email} />
                    </dd>
                  </div>
                )}

                {release.artists.user_unique_id && (
                  <div className="bg-blue-500/10 p-4 rounded-xl border border-blue-500/30">
                    <dt className="text-sm font-medium text-blue-300 mb-2">User Unique ID (API)</dt>
                    <dd className="text-blue-200 font-mono text-sm break-all flex items-center">
                      {release.artists.user_unique_id}
                      <CopyButton text={release.artists.user_unique_id} />
                    </dd>
                  </div>
                )}

                {/* Platform Hesapları */}
                {release.artists.is_artist_exist_on_platforms && (
                  <div className="col-span-2">
                    <div className="bg-gradient-to-r from-green-500/10 to-pink-500/10 p-4 rounded-xl border border-green-500/20">
                      <dt className="text-sm font-medium text-white mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                        </svg>
                        Platform Hesapları
                      </dt>
                      <div className="flex flex-wrap gap-3">
                        {release.artists.spotify_artist_profile_url && (
                          <a
                            href={release.artists.spotify_artist_profile_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-300 border border-green-600/30 rounded-lg transition-colors duration-200"
                          >
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                            </svg>
                            Spotify
                          </a>
                        )}
                        {release.artists.apple_music_artist_profile_url && (
                          <a
                            href={release.artists.apple_music_artist_profile_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-pink-600/20 hover:bg-pink-600/30 text-pink-300 border border-pink-600/30 rounded-lg transition-colors duration-200"
                          >
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.132c-.317-1.314-1.062-2.31-2.17-3.025a5.334 5.334 0 00-2.186-.681 39.246 39.246 0 00-2.956-.1h-7.64c-.92 0-1.841.024-2.759.1a5.4 5.4 0 00-2.186.681c-1.108.715-1.853 1.711-2.17 3.025a9.123 9.123 0 00-.24 2.132c-.024.776-.035 1.552-.035 2.328v7.096c0 .776.011 1.552.035 2.328.024.776.086 1.454.24 2.132.317 1.314 1.062 2.31 2.17 3.025a5.334 5.334 0 002.186.681c.92.076 1.839.1 2.759.1h7.64c.92 0 1.841-.024 2.759-.1a5.334 5.334 0 002.186-.681c1.108-.715 1.853-1.711 2.17-3.025.154-.678.216-1.356.24-2.132.024-.776.035-1.552.035-2.328V8.452c0-.776-.011-1.552-.035-2.328zm-4.165 6.155v.186a4.337 4.337 0 01-.748 2.549 3.511 3.511 0 01-2.757 1.48c-.895 0-1.648-.396-2.181-1.162-.591-.852-.895-1.915-.895-3.18V5.986h1.972v6.197c0 .936.155 1.647.437 2.139.296.484.724.739 1.307.739.486 0 .916-.196 1.241-.599.3-.36.451-.791.451-1.316v-7.16h1.972v4.943l.201-.014z"/>
                            </svg>
                            Apple Music
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* User Information */}
                {release.artists.user_id && (
                  <>
                    <div className="col-span-2 mt-4">
                      <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Kullanıcı Bilgileri
                      </h4>
                    </div>

                    <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                      <dt className="text-sm font-medium text-white/70 mb-2">User ID</dt>
                      <dd className="text-sm font-mono text-white break-all flex items-center">
                        {release.artists.user_id}
                        <CopyButton text={release.artists.user_id} />
                      </dd>
                    </div>

                    {release.artists.users && (
                      <>
                        {release.artists.users.email && (
                          <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                            <dt className="text-sm font-medium text-white/70 mb-2">Kullanıcı Email</dt>
                            <dd className="text-white font-mono text-sm break-all flex items-center">
                              {release.artists.users.email}
                              <CopyButton text={release.artists.users.email} />
                            </dd>
                          </div>
                        )}

                        {(release.artists.users.first_name || release.artists.users.last_name) && (
                          <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                            <dt className="text-sm font-medium text-white/70 mb-2">Kullanıcı Ad/Soyad</dt>
                            <dd className="text-lg font-semibold text-white flex items-center">
                              {[release.artists.users.first_name?.trim(), release.artists.users.last_name?.trim()].filter(Boolean).join(' ')}
                              <CopyButton text={[release.artists.users.first_name?.trim(), release.artists.users.last_name?.trim()].filter(Boolean).join(' ')} />
                            </dd>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Track Info */}
          <div className="glass-card">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
              </svg>
              {t('tracks.trackInfo')}
            </h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.trackName')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {track?.title}
                  {track?.title && <CopyButton text={track.title} />}
                </dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.duration')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {track?.duration}
                  {track?.duration && <CopyButton text={track.duration} />}
                </dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.trackOrder')}</dt>
                <dd className="text-lg font-semibold text-white">{track?.track_number || 1}</dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.trackLanguage')}</dt>
                <dd className="text-lg font-semibold text-white flex items-center">
                  {track?.language}
                  {track?.language && <CopyButton text={track.language} />}
                </dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.explicit')}</dt>
                <dd className="text-lg font-semibold text-white">
                  {track?.is_explicit ? t('common.yes') : t('common.no')}
                </dd>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-2">{t('tracks.hasLyricsLabel')}</dt>
                <dd className="text-lg font-semibold text-white">
                  {track?.has_lyrics ? t('tracks.exists') : t('tracks.notExists')}
                </dd>
              </div>
            </dl>
            
            {/* Audio Player */}
            {(audioFile || audioUrl) && (
              <div className="mt-6 bg-white/5 p-6 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                    {t('audioPlayer.preview')}
                  </div>
                  {isAdmin && audioUrl && (
                    <button
                      onClick={handleDownloadAudio}
                      className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg text-sm font-medium text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 transform hover:scale-105 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      {t('common.download')}
                    </button>
                  )}
                </dt>
                {audioUrl ? (
                  <AudioPlayer audioUrl={audioUrl} fileName={track?.title} />
                ) : (
                  <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-xl p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-blue-400 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-medium text-sm mb-2">
                          {track?.title} • {track?.duration}
                        </h4>
                        <div className="text-white/60 text-sm">
                          Audio file URL: {audioUrl}
                          <br />
                          <em>Note: CORS configuration needed on CloudFront for browser playback</em>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {track?.has_lyrics && track?.lyrics && (
              <div className="mt-6 bg-white/5 p-6 rounded-xl border border-white/10">
                <dt className="text-sm font-medium text-white/70 mb-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    {t('tracks.lyrics')}
                  </div>
                  <CopyButton text={cleanLyrics(track.lyrics)} />
                </dt>
                <dd className="text-white whitespace-pre-line leading-relaxed bg-black/20 p-4 rounded-lg">
                  {cleanLyrics(track.lyrics)}
                </dd>
              </div>
            )}
          </div>

          {/* AI Usage Info */}
          <div className="glass-card">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('admin.aiUsageInfo')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <span className="text-white font-medium">{t('admin.musicComposition')}</span>
                <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${
                  track?.ai_generated?.music ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-green-500/20 text-green-300 border-green-500/30'
                }`}>
                  {track?.ai_generated?.music ? t('admin.aiAssisted') : t('admin.humanMade')}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <span className="text-white font-medium">{t('tracks.lyrics')}</span>
                <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${
                  track?.ai_generated?.lyrics ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-green-500/20 text-green-300 border-green-500/30'
                }`}>
                  {track?.ai_generated?.lyrics ? t('admin.aiAssisted') : t('admin.humanMade')}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <span className="text-white font-medium">{t('admin.vocalPerformance')}</span>
                <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${
                  track?.ai_generated?.vocals ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-green-500/20 text-green-300 border-green-500/30'
                }`}>
                  {track?.ai_generated?.vocals ? t('admin.aiAssisted') : t('admin.humanMade')}
                </span>
              </div>
              {track?.ai_generated?.human_notes && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <dt className="text-sm font-medium text-white/70 mb-2">{t('admin.humanContributionDetails')}</dt>
                  <dd className="text-white leading-relaxed">{track.ai_generated.human_notes}</dd>
                </div>
              )}
            </div>
          </div>

          {/* Rights & Legal */}
          {(release.rights || (release.contributors && release.contributors.length > 0)) && (
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.414-4.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {t('tracks.rightsAndLegal')}
              </h3>
              <div className="space-y-4">
                {release.rights && (
                  <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                    <h4 className="text-lg font-medium text-white mb-3">{t('tracks.rights')}</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-white/70">ISRC</dt>
                        <dd className="text-white font-medium flex items-center">
                          {release.rights.isrc || t('messages.notSpecified')}
                          {release.rights.isrc && <CopyButton text={release.rights.isrc} />}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-white/70">UPC</dt>
                        <dd className="text-white font-medium flex items-center">
                          {release.rights.upc || t('messages.notSpecified')}
                          {release.rights.upc && <CopyButton text={release.rights.upc} />}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-white/70">{t('tracks.phonogramOwner')}</dt>
                        <dd className="text-white font-medium flex items-center">
                          {release.rights.phonogram_owner || t('messages.notSpecified')}
                          {release.rights.phonogram_owner && <CopyButton text={release.rights.phonogram_owner} />}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-white/70">{t('tracks.compositionOwner')}</dt>
                        <dd className="text-white font-medium flex items-center">
                          {release.rights.composition_owner || t('messages.notSpecified')}
                          {release.rights.composition_owner && <CopyButton text={release.rights.composition_owner} />}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
                
                {release.contributors && release.contributors.length > 0 && (
                  <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                    <h4 className="text-lg font-medium text-white mb-3">{t('tracks.contributors')}</h4>
                    <div className="space-y-2">
                      {release.contributors.map((contributor, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                          <span className="text-white flex items-center">
                            {contributor.name}
                            <CopyButton text={contributor.name} />
                          </span>
                          <span className="text-white/70 text-sm flex items-center">
                            {contributor.role}
                            <CopyButton text={contributor.role} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Revenue Sharing */}
          {release.split_pay && release.split_pay.length > 0 && (
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                {t('release.revenueSharing')}
              </h3>
              <div className="space-y-4">
                <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-200">
                        {t('release.revenueDescription')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {release.split_pay.map((split, index) => (
                    <div key={index} className="bg-white/5 p-4 rounded-xl border border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-sm font-medium text-white/70 mb-1">{t('admin.nameField')}</div>
                          <div className="text-white font-medium">{split.collaborator_name}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white/70 mb-1">{t('auth.email')}</div>
                          <div className="text-white font-medium">{split.email}</div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white/70 mb-1">{t('admin.sharePercent')}</div>
                          <div className="text-white font-medium">{split.percentage}%</div>
                        </div>
                      </div>
                      {split.role && (
                        <div className="mt-2">
                          <div className="text-sm font-medium text-white/70 mb-1">{t('admin.role')}</div>
                          <div className="text-white font-medium">{split.role}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Distribution Partners */}
          <div className="glass-card">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              {t('admin.distributionChannels')}
            </h3>
            {release.partners && release.partners.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {release.partners.map((partner, index) => (
                  <div key={index} className="p-4 bg-white/5 rounded-xl border border-white/10 text-center">
                    <span className="text-white font-medium">{partner}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-500/30 text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-2l2 2m-2 2l2-2M9 7l2 2m-2 2l2-2" />
                </svg>
                <p className="text-blue-300 font-medium">{t('admin.defaultDistribution')}</p>
                <p className="text-blue-200/70 text-sm mt-2">{t('admin.defaultDistributionNote')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Back to list button */}
      <div className="text-center">
        <Link to={backToPath} className="btn-secondary inline-flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {t(backToText)}
        </Link>
      </div>

      {/* Status Change Modal */}
      {isAdmin && onStatusChange && showStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900/95 backdrop-blur-lg border border-white/20 rounded-2xl p-6 w-full max-w-md mx-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Status Değiştir
              </h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-white/70 mb-4">
                "{release.title}" adlı şarkının yeni statusunu seçin:
              </p>

              <div className="space-y-2">
                <button
                  onClick={() => {
                    onStatusChange('draft');
                    setShowStatusModal(false);
                  }}
                  disabled={release.status === 'draft'}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    release.status === 'draft'
                      ? 'bg-white/5 text-white/40 cursor-not-allowed border-2 border-gray-500/50'
                      : 'bg-white/10 hover:bg-white/20 text-white border-2 border-transparent hover:border-gray-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`w-3 h-3 rounded-full mr-3 ${release.status === 'draft' ? 'bg-gray-400' : 'bg-gray-500'}`} />
                      <div>
                        <div className="font-medium">{t('status.draft')}</div>
                        <div className="text-xs text-white/60">Taslak olarak kaydet</div>
                      </div>
                    </div>
                    {release.status === 'draft' && (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => {
                    onStatusChange('pending');
                    setShowStatusModal(false);
                  }}
                  disabled={release.status === 'pending'}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    release.status === 'pending'
                      ? 'bg-white/5 text-white/40 cursor-not-allowed border-2 border-yellow-500/50'
                      : 'bg-white/10 hover:bg-white/20 text-white border-2 border-transparent hover:border-yellow-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`w-3 h-3 rounded-full mr-3 ${release.status === 'pending' ? 'bg-yellow-400' : 'bg-yellow-500'}`} />
                      <div>
                        <div className="font-medium">{t('status.pending')}</div>
                        <div className="text-xs text-white/60">Onay bekliyor</div>
                      </div>
                    </div>
                    {release.status === 'pending' && (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => {
                    onStatusChange('approved');
                    setShowStatusModal(false);
                  }}
                  disabled={release.status === 'approved'}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    release.status === 'approved'
                      ? 'bg-white/5 text-white/40 cursor-not-allowed border-2 border-green-500/50'
                      : 'bg-white/10 hover:bg-white/20 text-white border-2 border-transparent hover:border-green-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`w-3 h-3 rounded-full mr-3 ${release.status === 'approved' ? 'bg-green-400' : 'bg-green-500'}`} />
                      <div>
                        <div className="font-medium">{t('status.approved')}</div>
                        <div className="text-xs text-white/60">Onaylandı</div>
                      </div>
                    </div>
                    {release.status === 'approved' && (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => {
                    onStatusChange('submitted');
                    setShowStatusModal(false);
                  }}
                  disabled={release.status === 'submitted'}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    release.status === 'submitted'
                      ? 'bg-white/5 text-white/40 cursor-not-allowed border-2 border-blue-500/50'
                      : 'bg-white/10 hover:bg-white/20 text-white border-2 border-transparent hover:border-blue-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`w-3 h-3 rounded-full mr-3 ${release.status === 'submitted' ? 'bg-blue-400' : 'bg-blue-500'}`} />
                      <div>
                        <div className="font-medium">{t('status.submitted')}</div>
                        <div className="text-xs text-white/60">Platforma iletildi</div>
                      </div>
                    </div>
                    {release.status === 'submitted' && (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => {
                    onStatusChange('published');
                    setShowStatusModal(false);
                  }}
                  disabled={release.status === 'published'}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    release.status === 'published'
                      ? 'bg-white/5 text-white/40 cursor-not-allowed border-2 border-emerald-500/50'
                      : 'bg-white/10 hover:bg-white/20 text-white border-2 border-transparent hover:border-emerald-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`w-3 h-3 rounded-full mr-3 ${release.status === 'published' ? 'bg-emerald-400' : 'bg-emerald-500'}`} />
                      <div>
                        <div className="font-medium">{t('status.published')}</div>
                        <div className="text-xs text-white/60">Yayınlandı</div>
                      </div>
                    </div>
                    {release.status === 'published' && (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>

                <div className="border-t border-white/10 my-3" />

                <button
                  onClick={() => {
                    onStatusChange('rejected');
                    setShowStatusModal(false);
                  }}
                  disabled={release.status === 'rejected'}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    release.status === 'rejected'
                      ? 'bg-white/5 text-white/40 cursor-not-allowed border-2 border-red-500/50'
                      : 'bg-red-500/10 hover:bg-red-500/20 text-red-300 border-2 border-transparent hover:border-red-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`w-3 h-3 rounded-full mr-3 ${release.status === 'rejected' ? 'bg-red-400' : 'bg-red-500'}`} />
                      <div>
                        <div className="font-medium">{t('status.rejected')}</div>
                        <div className="text-xs text-red-200/60">Reddedildi</div>
                      </div>
                    </div>
                    {release.status === 'rejected' && (
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-white/10">
              <button
                onClick={() => setShowStatusModal(false)}
                className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white font-medium transition-all duration-200"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackDetailView; 